// Domain repository: translates between the public app shape (same as the
// old FastAPI contract) and the NocodeBackend row format.
//
// Row mapping:
//   questions:  id↔question_uuid, order↔order_num, type↔question_type,
//               choices↔JSON string
//   sessions:   id↔session_uuid, completed↔0/1
//   responses:  question_id↔question_uuid,
//               choice_selected / text_response / audio_url / choices_offered (JSON) store answer values
//   settings:   singleton row (first one); robot_texts / ui_texts are JSON strings

import {
  nbCreate, nbRead, nbFindOne, nbUpdate, nbDelete, getAuthMode, ncbSignInEmail,
} from './nocodebackend';
import {
  migrateQuestionFromStore,
  normalizeChoicesArray,
} from '@/lib/questionChoices';

// ---------- Defaults (kept in sync with what used to live in server.py) ----------

export const DEFAULT_ROBOT_TEXTS = {
  welcome: "Salut ! Je suis ton ami robot. Ici, tu peux me parler de tout. C'est notre secret.",
  ask_name: "Comment tu t'appelles ? Écris ton prénom dans la case.",
  ask_name_audio: "Super ! Maintenant, appuie sur le gros bouton bleu pour enregistrer ton prénom avec ta voix. Quand tu as fini, appuie sur le bouton rouge.",
  ask_name_audio_skip: "Pas de souci ! On continue.",
  // name_success : utiliser {prénom} ou {prenom} pour le prénom saisi (page identité autonome).
  name_success: "Cool {prénom} ! J'aime bien ce prénom !",
  // Après enregistrement vocal du prénom (questionnaire).
  identity_voice_done: "Super ! On y va !",
  question_intro: "Je vais te poser quelques questions. Il n'y a pas de bonne ou mauvaise réponse. Es-tu prêt ?",
  read_choices: "Voici les réponses possibles. Appuie sur un bouton pour l'écouter à nouveau.",
  thank_response: "Merci ! Tu es super !",
  goodbye: "Merci d'avoir parlé avec moi ! Tu as été super courageux ! À bientôt !",
};

export const DEFAULT_UI_TEXTS = {
  welcome_title: "Salut !",
  welcome_subtitle: "Je suis ton ami robot. Ici, tu peux me parler de tout.",
  intro_title: "Introduction",
  intro_message: "",
  intro_button: "Continuer",
  start_button: "C'est parti !",
  name_title: "Comment tu t'appelles ?",
  name_placeholder: "Ton prénom...",
  name_button: "C'est moi !",
  voice_title: "Dis-moi ton prénom !",
  voice_instruction: "Appuie sur le micro et parle",
  voice_listening: "Je t'écoute...",
  voice_recorded: "J'ai bien entendu !",
  continue_button: "Continuer",
  skip_link: "Passer cette étape",
  send_button: "C'est bon !",
  skip_question: "Passer",
  thank_title: "Merci !",
  thank_message: "Tu as été super courageux de me parler.",
  thank_proud: "Je suis fier de toi !",
  home_button: "Retour",
  /** Instructions micro (complément QCM voix) — éviter « envoyer » pour l’enfant. */
  choice_voice_stop_sends: 'Quand tu as fini de me parler, touche le bouton rouge.',
  choice_voice_record_then_stop:
    'Touche le micro pour me parler, puis le bouton rouge quand tu as terminé.',
  /** Texte stocké en base (compte rendu) si l’enfant passe l’enregistrement vocal après un choix QCM avec suite vocale. */
  mc_voice_skipped_note:
    "L'enfant a passé l'enregistrement vocal après avoir sélectionné sa réponse au QCM ; le choix enregistré correspond au bouton sur lequel il a cliqué.",
};

const DEFAULT_QUESTIONS = [
  { text: "Comment tu t'appelles ?", robot_text: "Comment tu t'appelles ? Écris ton prénom !",
    type: "identity", choices: [], order: 0,
    thank_text: "Cool ! J'aime bien ce prénom !" },
  { text: "Comment tu te sens aujourd'hui ?", robot_text: "Comment tu te sens aujourd'hui ?",
    type: "multiple_choice",
    choices: ["Content", "Un peu triste", "Fatigué", "En colère"], order: 1 },
  { text: "Qu'est-ce que tu aimes faire ?",
    robot_text: "Qu'est-ce que tu aimes faire quand tu es à la maison ?",
    type: "multiple_choice",
    choices: ["Jouer", "Dessiner", "Regarder des dessins animés", "Être avec ma famille"],
    order: 2 },
  { text: "Comment ça se passe à l'école ?",
    robot_text: "Comment ça se passe à l'école en ce moment ?",
    type: "multiple_choice",
    choices: [
      { label: "Très bien", voice_followup: false, followup_robot_text: '' },
      { label: "Ça va", voice_followup: false, followup_robot_text: '' },
      {
        label: "Pas très bien",
        voice_followup: true,
        followup_robot_text: "Tu veux m'en dire un peu plus ?",
      },
      {
        label: "Je ne sais pas",
        voice_followup: true,
        followup_robot_text: "Tu veux m'en dire un peu plus ?",
      },
    ],
    order: 3 },
  { text: "Est-ce qu'il y a quelque chose qui te fait peur ?",
    robot_text: "Est-ce qu'il y a quelque chose qui te fait peur en ce moment ?",
    type: "open_voice", choices: [], order: 4 },
  { text: "Est-ce que quelque chose te rend triste ?",
    robot_text: "Est-ce que quelque chose te rend triste à l'école ou à la maison ?",
    type: "open_voice", choices: [], order: 5 },
];

const uuid = () => crypto.randomUUID();

// ---------- Converters ----------

function rowToQuestion(row) {
  let choices = [];
  try { choices = JSON.parse(row.choices || '[]'); } catch { choices = []; }
  const base = {
    id: row.question_uuid || '',
    text: row.text || '',
    robot_text: row.robot_text || '',
    type: row.question_type || 'multiple_choice',
    choices: Array.isArray(choices) ? choices : [],
    order: parseInt(row.order_num || 0, 10),
    encouragement_text: row.encouragement_text || null,
    thank_text: row.thank_text || null,
    created_at: row.created_at || '',
    _dbId: row.id,  // internal NocodeBackend integer id
  };
  return migrateQuestionFromStore(base);
}

function questionToRow(q) {
  const out = {};
  if (q.id !== undefined) out.question_uuid = q.id;
  if (q.text !== undefined) out.text = q.text;
  if (q.robot_text !== undefined) out.robot_text = q.robot_text;
  if (q.order !== undefined) out.order_num = parseInt(q.order, 10);
  if ('encouragement_text' in q) out.encouragement_text = q.encouragement_text;
  if ('thank_text' in q) out.thank_text = q.thank_text;

  if (q.type !== undefined) {
    out.question_type = q.type === 'multiple_choice_voice' ? 'multiple_choice' : q.type;
  }

  if (q.choices !== undefined) {
    const effType =
      q.type === 'multiple_choice_voice' ? 'multiple_choice' : (q.type || 'multiple_choice');
    if (effType === 'multiple_choice') {
      let ch = q.choices;
      if (q.type === 'multiple_choice_voice') {
        ch = normalizeChoicesArray(ch).map((c) => ({ ...c, voice_followup: true }));
      } else {
        ch = normalizeChoicesArray(ch);
      }
      out.choices = JSON.stringify(ch);
    } else {
      out.choices = JSON.stringify(Array.isArray(q.choices) ? q.choices : []);
    }
  }
  return out;
}

function parseChoicesOffered(raw) {
  if (raw == null || raw === '') return null;
  if (Array.isArray(raw) && raw.length > 0) return raw.map((x) => String(x));
  if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw);
      if (Array.isArray(p) && p.length > 0) return p.map((x) => String(x));
    } catch {
      return null;
    }
  }
  return null;
}

function rowToResponse(row) {
  let choice_selected = row.choice_selected ?? null;
  let text_response = row.text_response ?? null;
  return {
    question_id: row.question_uuid || '',
    question_text: row.question_text || '',
    choice_selected,
    text_response,
    audio_url: row.audio_url || null,
    choices_offered: parseChoicesOffered(row.choices_offered),
  };
}

function responseToRow(sessionUuid, r) {
  const out = {
    session_uuid: sessionUuid,
    question_uuid: r.question_id || '',
    question_text: r.question_text || '',
    choice_selected: r.choice_selected ?? null,
    text_response: r.text_response ?? null,
    audio_url: r.audio_url || '',
  };
  const labels = parseChoicesOffered(r.choices_offered);
  if (labels && labels.length) {
    out.choices_offered = JSON.stringify(labels);
  }
  return out;
}

async function hydrateSession(row) {
  const sessionUuid = row.session_uuid || '';
  let respRows = await nbRead('responses', { session_uuid: sessionUuid }, 1000);
  // NocodeBackend can lag a bit on read-after-write; retry briefly when empty.
  if (respRows.length === 0) {
    for (let i = 0; i < 2; i++) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 600));
      // eslint-disable-next-line no-await-in-loop
      respRows = await nbRead('responses', { session_uuid: sessionUuid }, 1000);
      if (respRows.length > 0) break;
    }
  }
  const name_audio_url = row.child_name_audio_url || null;
  const responses = respRows.map(rowToResponse);
  return {
    id: sessionUuid,
    child_name: row.child_name || '',
    child_name_audio_url: name_audio_url,
    responses,
    theme: row.theme || 'default',
    completed: Boolean(row.completed),
    created_at: row.created_at || '',
    _dbId: row.id,
  };
}

function rowToSettings(row) {
  let robot_texts = {};
  let ui_texts = {};
  try { robot_texts = JSON.parse(row.robot_texts || '{}') || {}; } catch { robot_texts = {}; }
  try { ui_texts = JSON.parse(row.ui_texts || '{}') || {}; } catch { ui_texts = {}; }
  return {
    _dbId: row.id,
    default_theme: row.default_theme || 'default',
    robot_texts: { ...DEFAULT_ROBOT_TEXTS, ...robot_texts },
    ui_texts: { ...DEFAULT_UI_TEXTS, ...ui_texts },
  };
}

// ---------- Settings ----------

export async function getOrCreateSettings() {
  const rows = await nbRead('settings', {}, 1);
  if (rows[0]) return rowToSettings(rows[0]);
  await nbCreate('settings', {
    admin_password: '',
    default_theme: 'default',
    robot_texts: JSON.stringify(DEFAULT_ROBOT_TEXTS),
    ui_texts: JSON.stringify(DEFAULT_UI_TEXTS),
  });
  const fresh = await nbRead('settings', {}, 1);
  return fresh[0] ? rowToSettings(fresh[0]) : {
    default_theme: 'default',
    robot_texts: DEFAULT_ROBOT_TEXTS,
    ui_texts: DEFAULT_UI_TEXTS,
  };
}

export async function updateSettings(patch) {
  const current = await getOrCreateSettings();
  const dbPatch = {};
  const nextState = { ...current };
  if (patch.default_theme !== undefined) {
    dbPatch.default_theme = patch.default_theme;
    nextState.default_theme = patch.default_theme;
  }
  if (patch.robot_texts !== undefined) {
    const merged = { ...current.robot_texts, ...patch.robot_texts };
    dbPatch.robot_texts = JSON.stringify(merged);
    nextState.robot_texts = merged;
  }
  if (patch.ui_texts !== undefined) {
    const merged = { ...current.ui_texts, ...patch.ui_texts };
    dbPatch.ui_texts = JSON.stringify(merged);
    nextState.ui_texts = merged;
  }
  if (Object.keys(dbPatch).length) {
    await nbUpdate('settings', current._dbId, dbPatch);
  }
  return nextState;
}

export async function verifyAdminCredentials({ password, email }) {
  const { mode } = await getAuthMode();
  if (mode !== "ncb") {
    return {
      ok: false,
      detail: 'Authentification NoCodeBackend inactive sur le serveur (variables Netlify).',
    };
  }
  if (!String(email || '').trim() || !password) {
    return { ok: false, detail: 'Indiquez l’e-mail et le mot de passe.' };
  }
  return ncbSignInEmail(String(email).trim(), password);
}

// ---------- Questions ----------

async function seedDefaultQuestionsIfEmpty() {
  const rows = await nbRead('questions', {}, 1);
  if (rows.length) return;
  // Sequential to avoid NocodeBackend burst limits
  for (const q of DEFAULT_QUESTIONS) {
    const withId = { ...q, id: uuid() };
    // eslint-disable-next-line no-await-in-loop
    await nbCreate('questions', questionToRow(withId));
    // eslint-disable-next-line no-await-in-loop
    await new Promise((r) => setTimeout(r, 250));
  }
}

export async function listQuestions() {
  await seedDefaultQuestionsIfEmpty();
  const rows = await nbRead('questions', {}, 1000);
  const questions = rows.map(rowToQuestion);
  questions.sort((a, b) => a.order - b.order);
  return questions;
}

export async function createQuestion(data) {
  const full = {
    id: uuid(),
    text: data.text || '',
    robot_text: data.robot_text || '',
    type: data.type || 'multiple_choice',
    choices: data.choices || [],
    order: data.order ?? 0,
    encouragement_text: null,
    thank_text: data.thank_text ?? null,
  };
  const migrated = migrateQuestionFromStore(full);
  await nbCreate('questions', questionToRow(migrated));
  return { ...migrated, id: full.id, created_at: new Date().toISOString() };
}

export async function updateQuestion(questionId, patch) {
  const row = await nbFindOne('questions', { question_uuid: questionId });
  if (!row) throw new Error('Question non trouvée');
  const dbPatch = questionToRow(patch);
  delete dbPatch.question_uuid;  // never overwrite uuid
  if (Object.keys(dbPatch).length) {
    await nbUpdate('questions', row.id, dbPatch);
  }
  return rowToQuestion({ ...row, ...dbPatch });
}

export async function deleteQuestion(questionId) {
  const row = await nbFindOne('questions', { question_uuid: questionId });
  if (!row) return false;
  await nbDelete('questions', row.id);
  return true;
}

export async function reorderQuestions(ids) {
  const rows = await nbRead('questions', {}, 1000);
  const byUuid = new Map(rows.map((row) => [row.question_uuid, row]));
  for (let i = 0; i < ids.length; i++) {
    const row = byUuid.get(ids[i]);
    const nextOrder = i + 1;
    // Evite les updates inutiles (et donc la latence évitable).
    if (!row || parseInt(row.order_num || 0, 10) === nextOrder) continue;
    // eslint-disable-next-line no-await-in-loop
    await nbUpdate('questions', row.id, { order_num: nextOrder });
  }
}

// ---------- Sessions ----------

export async function listSessions() {
  let rows = await nbRead('sessions', {}, 1000);
  // Same read-after-write lag as elsewhere: first read can briefly return [].
  if (rows.length === 0) {
    for (let i = 0; i < 2; i++) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise((r) => setTimeout(r, 500));
      // eslint-disable-next-line no-await-in-loop
      rows = await nbRead('sessions', {}, 1000);
      if (rows.length > 0) break;
    }
  }
  rows.sort((a, b) => String(b.created_at || '').localeCompare(String(a.created_at || '')));
  return Promise.all(rows.map(hydrateSession));
}

export async function getSession(sessionId) {
  const row = await nbFindOne('sessions', { session_uuid: sessionId });
  if (!row) return null;
  return hydrateSession(row);
}

export async function createSession(childName, theme = 'default') {
  const id = uuid();
  await nbCreate('sessions', {
    session_uuid: id,
    child_name: childName,
    theme,
    completed: 0,
  });
  return {
    id,
    child_name: childName,
    child_name_audio_url: null,
    responses: [],
    theme,
    completed: false,
    created_at: new Date().toISOString(),
  };
}

export async function setSessionCompleted(sessionId, completed) {
  const row = await nbFindOne('sessions', { session_uuid: sessionId });
  if (!row) throw new Error('Session non trouvée');
  await nbUpdate('sessions', row.id, { completed: completed ? 1 : 0 });
}

export async function setSessionNameAudio(sessionId, audioUrl) {
  const sessionRow = await nbFindOne('sessions', { session_uuid: sessionId });
  if (!sessionRow) throw new Error('Session non trouvée');
  await nbUpdate('sessions', sessionRow.id, { child_name_audio_url: audioUrl });
}

export async function addResponse(sessionId, response) {
  const row = responseToRow(sessionId, response);
  try {
    await nbCreate('responses', row);
  } catch (e) {
    if (row.choices_offered == null) throw e;
    const rest = { ...row };
    delete rest.choices_offered;
    await nbCreate('responses', rest);
  }
}

export async function deleteSession(sessionId) {
  const row = await nbFindOne('sessions', { session_uuid: sessionId });
  if (!row) return false;
  const respRows = await nbRead('responses', { session_uuid: sessionId }, 1000);
  // Suppression par lots pour réduire fortement la latence perçue,
  // tout en évitant un burst trop agressif vers l'API.
  const BATCH_SIZE = 8;
  for (let i = 0; i < respRows.length; i += BATCH_SIZE) {
    const batch = respRows.slice(i, i + BATCH_SIZE);
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(batch.map((rr) => nbDelete('responses', rr.id)));
  }
  await nbDelete('sessions', row.id);
  return true;
}

// ---------- Audio upload ----------
// Since NocodeBackend has no file storage in this plan, we just wrap the
// raw base64 into a data URL that can be stored as-is in responses.audio_url
// and played back directly in <audio src={url}>.
export function buildAudioDataUrl(base64) {
  if (!base64) return null;
  const raw = base64.includes(',') ? base64.split(',', 2)[1] : base64;
  return `data:audio/webm;base64,${raw}`;
}
